/**
 * The settings page described as data: which sections exist for an account
 * and for a location, how they are grouped, who may open them, how they save,
 * and which tabs they hold. The page frame, its search, the phone list and
 * links all read this one description, so a section is added in one place.
 */

export type SettingsScopeKind = 'account' | 'location';

/** Groups in display order. '' is the ungrouped top of the list (Overview, Location customizations). */
export const SETTINGS_GROUPS = ['', 'Business', 'Operations', 'Money', 'Connections'] as const;
export type SettingsGroup = (typeof SETTINGS_GROUPS)[number];

/**
 * How a section saves. `explicit`: edits wait for Save changes. `instant`:
 * every change saves as it is made. `mixed`: both kinds of panel sit in it.
 * `checked`: saved by the section's own review (Check what changes, then
 * Save), never by the page's Save changes; its unsaved edits still count
 * when leaving the page. `none`: read-only summaries.
 */
export type SettingsSaveMode = 'explicit' | 'instant' | 'mixed' | 'checked' | 'none';

/** What the registry needs to know about the viewer. */
export interface SettingsAccess {
  /** False while creating: only the profile shows. */
  isEditMode: boolean;
  isSystemAdmin: boolean;
  permissions: readonly string[];
  /** Account scope only: account admins or any reports:*:account permission. */
  canViewAccountReports?: boolean;
  /**
   * Location scope only: false for a location with no account (legacy data).
   * Pricing resolves the account's values, so it needs one. Default: true.
   */
  hasAccount?: boolean;
}

export interface SettingsTabDef {
  id: string;
  label: string;
  /** Hidden when false. Default: shown. */
  visible?: (access: SettingsAccess) => boolean;
  /** Shown read-only (a lock beside the label) when true. */
  locked?: (access: SettingsAccess) => boolean;
  /** Extra words people search with ("geofence", "overtime"). */
  keywords?: readonly string[];
}

export interface SettingsSectionDef {
  id: string;
  label: string;
  group: SettingsGroup;
  scope: SettingsScopeKind;
  save: SettingsSaveMode;
  /** Hidden when false. Default: shown. */
  visible?: (access: SettingsAccess) => boolean;
  /** Tabs in display order; empty or missing = no tab row. */
  tabs?: readonly SettingsTabDef[];
  keywords?: readonly string[];
}

/** A section as one viewer sees it: only the tabs they may open. */
export interface VisibleSettingsSection extends Omit<SettingsSectionDef, 'tabs' | 'visible'> {
  tabs: Array<{ id: string; label: string; locked: boolean; keywords?: readonly string[] }>;
}
